/** @jsxImportSource react */
'use client';

import { useEffect } from 'react';

import Script from 'next/script';

// Help Scout Beacon — in-page support widget that surfaces docs, chat, and
// ticket submission. ID is hard-coded to match
// frontend/components/global/HeadScripts.tsx so support conversations from
// marketing and the product app land in the same Help Scout inbox.
// Window.Beacon is declared in utils/beacon.ts.

const BEACON_ID = 'da166cf3-5feb-44d9-a2fe-757a9b8eefad';

// Brand config — Help Scout calls these "display options" on the Beacon.
// Colors must also be set inside the Help Scout admin (Manage → Beacons →
// Customize → Color), but we set them here as a redundant fallback in case
// the admin config drifts. Amethyst matches our primary CTA color.
const BEACON_CONFIG = {
  display: {
    color: '#7B2CFF',
    // amethyst
    position: 'right' as const,

    style: 'iconAndText' as const,
    text: 'Help',
  },
  messaging: {
    chatEnabled: true,
    contactForm: { showName: true },
  },
};

export function Beacon() {
  useEffect(() => {
    // Initialize after the loader script runs. Help Scout polls for the
    // readyQueue so calls before the script loads still apply.
    const interval = window.setInterval(() => {
      if (typeof window.Beacon === 'function') {
        window.Beacon('init', BEACON_ID);
        window.Beacon('config', BEACON_CONFIG);
        window.clearInterval(interval);
      }
    }, 100);

    // Stop polling after 10s if Beacon never loaded (ad blocker, network, etc.)
    const timeout = window.setTimeout(
      () => window.clearInterval(interval),
      10000
    );

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    // Official Help Scout loader, minified. Inserts beacon-v2.helpscout.net
    // and exposes window.Beacon with a readyQueue.
    <Script
      dangerouslySetInnerHTML={{
        __html: `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});`,
      }}
      id="helpscout-beacon-loader"
      strategy="afterInteractive"
    />
  );
}

/** Programmatically open the Beacon from anywhere (e.g. a "Live chat" button). */
export function openBeacon() {
  if (typeof window === 'undefined') return;
  window.Beacon?.('open');
}

/** Prefill the contact form with subject/message. Useful for context-aware support. */
export function openBeaconWithMessage(subject: string, text?: string) {
  if (typeof window === 'undefined') return;
  window.Beacon?.('prefill', { subject, text: text ?? '' });
  window.Beacon?.('navigate', '/ask/message/');
  window.Beacon?.('open');
}
